import type { IconNames } from '@repo/foundations';
import { WidgetSize, WidgetType } from '../../../../interfaces';
import type { HomepageWidgetType } from '../../types';

export const WIDGET_HEIGHT = 360;
export const WIDGET_HEADER_HEIGHT = 53;
export const WIDGET_CONTENT_HEIGHT =
	WIDGET_HEIGHT - WIDGET_HEADER_HEIGHT - 2 * 16;

// Keeping the legacy widget sizes
export const WIDGET_SIZE_MAP: Record<WidgetSize, string> = {
	[WidgetSize.SMALL]: '6',
	[WidgetSize.MEDIUM]: '6',
	[WidgetSize.LARGE]: '12',
	[WidgetSize.FULL]: '12',
};

export const WIDGET_TITLE_MAP: Record<HomepageWidgetType, string> = {
	[WidgetType.HELPFUL_LINKS]: 'Helpful links',
	[WidgetType.INTEGRATIONS]: 'Integrations',
	[WidgetType.TEXT_BLOCK]: '',
	[WidgetType.INBOX]: 'Inbox',
	[WidgetType.ANNOUNCEMENTS]: 'Announcements',
	[WidgetType.PINNED_COLLECTIONS]: 'Collections',
	[WidgetType.POPULAR_COLLECTIONS]: 'Collections',
	[WidgetType.BOOKMARKED_COLLECTIONS]: 'Collections',
	[WidgetType.BOOKMARKED_RESOURCES]: 'Resources',
	[WidgetType.POPULAR_RESOURCES]: 'Resources',
	[WidgetType.LOWEST_QUALITY_SCORE]: 'Resources',
	[WidgetType.RECENTLY_EDITED_RESOURCES]: 'Resources',
	[WidgetType.RECENTLY_ACCESSED_RESOURCES]: 'Resources',
	[WidgetType.RECENT_SEARCHES]: 'Searches',
	[WidgetType.QUESTIONS_POPULAR]: 'Questions',
	[WidgetType.QUESTIONS_BOOKMARKED]: 'Questions',
	[WidgetType.QUESTIONS_RECENTLY_EDITED]: 'Questions',
	[WidgetType.QUESTIONS_RECENTLY_ACCESSED]: 'Questions',
	[WidgetType.METRIC_LINE_CHART]: '',
	[WidgetType.METRIC_RESOURCE_TABLE]: '',
	[WidgetType.METRIC_USER_TABLE]: '',
	[WidgetType.METRIC_GROUP_TABLE]: '',
	[WidgetType.METRIC_SEARCH_TERM_TABLE]: '',
	[WidgetType.METRIC_TAG_TABLE]: '',
	[WidgetType.METRIC_BAR_CHART]: '',
	[WidgetType.METRIC_SINGLE_VALUE]: '',
	[WidgetType.METRIC_PIE_CHART]: '',
	[WidgetType.COLLECTION_RESOURCES]: '',
	[WidgetType.DATA_QUALITY_SCORE]: 'Quality score',
	[WidgetType.MONITOR]: 'Monitor',
	[WidgetType.AI_CHAT_HISTORY]: 'AI chat history',
	[WidgetType.METRIC_QUERIES_TABLE]: 'Resources',
	[WidgetType.AI_GLOSSARY_SUGGESTIONS]: 'Suggested glossaries',
	[WidgetType.AI_MONITOR_SUGGESTIONS]: 'Suggested monitors',
	[WidgetType.AI_DESCRIPTION_SUGGESTIONS]: 'Suggested descriptions',
};

export const WIDGET_ICON_MAP: Record<HomepageWidgetType, IconNames> = {
	[WidgetType.HELPFUL_LINKS]: 'externalLink',
	[WidgetType.INTEGRATIONS]: 'plugConnected',
	[WidgetType.TEXT_BLOCK]: 'note',
	[WidgetType.INBOX]: 'inbox',
	[WidgetType.ANNOUNCEMENTS]: 'speakerphone',
	[WidgetType.PINNED_COLLECTIONS]: 'folders',
	[WidgetType.POPULAR_COLLECTIONS]: 'folders',
	[WidgetType.BOOKMARKED_COLLECTIONS]: 'folders',
	[WidgetType.BOOKMARKED_RESOURCES]: 'files',
	[WidgetType.POPULAR_RESOURCES]: 'files',
	[WidgetType.LOWEST_QUALITY_SCORE]: 'progressCheck',
	[WidgetType.RECENTLY_EDITED_RESOURCES]: 'files',
	[WidgetType.RECENTLY_ACCESSED_RESOURCES]: 'files',
	[WidgetType.RECENT_SEARCHES]: 'search',
	[WidgetType.QUESTIONS_POPULAR]: 'messageCircleQuestion',
	[WidgetType.QUESTIONS_BOOKMARKED]: 'messageCircleQuestion',
	[WidgetType.QUESTIONS_RECENTLY_EDITED]: 'messageCircleQuestion',
	[WidgetType.QUESTIONS_RECENTLY_ACCESSED]: 'messageCircleQuestion',
	[WidgetType.METRIC_LINE_CHART]: 'chartLine',
	[WidgetType.METRIC_RESOURCE_TABLE]: 'chartLine',
	[WidgetType.METRIC_USER_TABLE]: 'chartLine',
	[WidgetType.METRIC_GROUP_TABLE]: 'chartLine',
	[WidgetType.METRIC_SEARCH_TERM_TABLE]: 'chartLine',
	[WidgetType.METRIC_TAG_TABLE]: 'chartLine',
	[WidgetType.METRIC_BAR_CHART]: 'chartLine',
	[WidgetType.METRIC_SINGLE_VALUE]: 'chartLine',
	[WidgetType.METRIC_PIE_CHART]: 'chartLine',
	[WidgetType.COLLECTION_RESOURCES]: 'files',
	[WidgetType.DATA_QUALITY_SCORE]: 'progressCheck',
	[WidgetType.MONITOR]: 'activity',
	[WidgetType.AI_CHAT_HISTORY]: 'sparkles',
	[WidgetType.METRIC_QUERIES_TABLE]: 'chartLine',
	[WidgetType.AI_GLOSSARY_SUGGESTIONS]: 'sparkles',
	[WidgetType.AI_MONITOR_SUGGESTIONS]: 'sparkles',
	[WidgetType.AI_DESCRIPTION_SUGGESTIONS]: 'sparkles',
};

export const RESOURCE_WIDGETS = [
	WidgetType.BOOKMARKED_RESOURCES,
	WidgetType.POPULAR_RESOURCES,
	WidgetType.LOWEST_QUALITY_SCORE,
	WidgetType.RECENTLY_EDITED_RESOURCES,
	WidgetType.RECENTLY_ACCESSED_RESOURCES,
] as const;

export const AI_SUGGESTIONS_WIDGETS = [
	WidgetType.AI_GLOSSARY_SUGGESTIONS,
	WidgetType.AI_MONITOR_SUGGESTIONS,
	WidgetType.AI_DESCRIPTION_SUGGESTIONS,
] as const;

export const AI_SUGGESTIONS_WIDGET_FILTERS = [
	{
		value: WidgetType.AI_GLOSSARY_SUGGESTIONS,
		label: 'Glossary',
	},
	{
		value: WidgetType.AI_MONITOR_SUGGESTIONS,
		label: 'Monitor',
	},
	{
		value: WidgetType.AI_DESCRIPTION_SUGGESTIONS,
		label: 'Description',
	},
];

export const RESOURCE_WIDGET_FILTERS = [
	{
		value: WidgetType.BOOKMARKED_RESOURCES,
		label: 'Bookmarked',
	},
	{
		value: WidgetType.POPULAR_RESOURCES,
		label: 'Popular',
	},
	{
		value: WidgetType.RECENTLY_ACCESSED_RESOURCES,
		label: 'Recently viewed',
	},
	{
		value: WidgetType.RECENTLY_EDITED_RESOURCES,
		label: 'Recently edited',
	},
	{
		value: WidgetType.LOWEST_QUALITY_SCORE,
		label: 'Lowest quality score',
	},
];

export const QUESTIONS_WIDGET_FILTERS = [
	{
		value: WidgetType.QUESTIONS_BOOKMARKED,
		label: 'Bookmarked',
	},
	{
		value: WidgetType.QUESTIONS_POPULAR,
		label: 'Popular',
	},
	{
		value: WidgetType.QUESTIONS_RECENTLY_ACCESSED,
		label: 'Recently viewed',
	},
	{
		value: WidgetType.QUESTIONS_RECENTLY_EDITED,
		label: 'Recently edited',
	},
];

export const QUESTIONS_WIDGETS = [
	WidgetType.QUESTIONS_BOOKMARKED,
	WidgetType.QUESTIONS_POPULAR,
	WidgetType.QUESTIONS_RECENTLY_EDITED,
	WidgetType.QUESTIONS_RECENTLY_ACCESSED,
] as const;

export const COLLECTION_WIDGETS = [
	WidgetType.POPULAR_COLLECTIONS,
	WidgetType.BOOKMARKED_COLLECTIONS,
] as const;

export const COLLECTION_WIDGET_FILTERS = [
	{
		value: WidgetType.POPULAR_COLLECTIONS,
		label: 'Popular',
	},
	{
		value: WidgetType.BOOKMARKED_COLLECTIONS,
		label: 'Bookmarked',
	},
];
